/**
 * A small markdown, for review notes.
 *
 * WHY WRITE ONE
 *   Lattice has no markdown dependency and does not want one. The subset an
 *   engineering note actually uses is small, and a parser we own is a parser we
 *   can make safe BY CONSTRUCTION: this one produces DATA, never HTML, so the
 *   product contains no `dangerouslySetInnerHTML` at all and no escaping bug is
 *   possible. CommentBody turns the data into React elements, and React escapes
 *   text for us.
 *
 *   The dialect is deliberately not CommonMark. Two departures matter, and both
 *   come from what these people actually type:
 *
 *   - A single newline is a LINE BREAK, not a space. A comment box is a chat
 *     box; nobody types a blank line between two lines of a list of concerns.
 *   - "_" is NOT emphasis. Every other filename here is
 *     "30kV_Cathode Cup_Roof_sld_2024.SLDPRT", and a renderer that italicises
 *     the middle of a part number is worse than one with no emphasis at all.
 *     "*italic*" and "**bold**" are the whole of it.
 *
 * DATA DEFINITIONS
 *
 *   An Inline is one of:
 *     { kind: "text",    text }            plain words
 *     { kind: "code",    text }            `verbatim`, never re-parsed
 *     { kind: "strong",  children }        **bold**
 *     { kind: "em",      children }        *italic*
 *     { kind: "link",    href, children }  [label](url) or a bare URL
 *     { kind: "mention", userId, text }    @jishnu — the person, not the word
 *     { kind: "file",    path }            [[a/b.STEP]] — see comment.ts
 *     { kind: "break" }                    a newline inside a paragraph
 *   Interpretation: one run of a line, already decided. Nothing downstream has
 *   to know markdown; it only has to know how to draw these eight things.
 *
 *   A Block is one of:
 *     { kind: "paragraph", children }
 *     { kind: "heading",   level, children }   level is 1..6
 *     { kind: "code",      text, lang }        a fenced block
 *     { kind: "quote",     blocks }            > quoted, and nestable
 *     { kind: "list",      ordered, start, items }
 *   Interpretation: one paragraph-level thing. A list item holds Inlines, not
 *   Blocks: nested lists and multi-paragraph items are out of scope, because
 *   they do not appear in review notes and every one of them is another way to
 *   render something surprising.
 *
 *   An Href is a String that has passed `safeHref`.
 *   Interpretation: a URL we are willing to put in front of a person. Anything
 *   that is not plainly http, https or mailto is not a link at all and stays on
 *   screen as the text the author wrote.
 */
import {
  segmentBody, parseFileRefPath, FILE_REF_OPEN, FILE_REF_CLOSE,
  type Path, type UserId,
} from '@lattice/core'

export type Inline =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'code'; readonly text: string }
  | { readonly kind: 'strong'; readonly children: readonly Inline[] }
  | { readonly kind: 'em'; readonly children: readonly Inline[] }
  | { readonly kind: 'link'; readonly href: string; readonly children: readonly Inline[] }
  | { readonly kind: 'mention'; readonly userId: UserId; readonly text: string }
  | { readonly kind: 'file'; readonly path: Path }
  | { readonly kind: 'break' }

export type Block =
  | { readonly kind: 'paragraph'; readonly children: readonly Inline[] }
  | { readonly kind: 'heading'; readonly level: number; readonly children: readonly Inline[] }
  | { readonly kind: 'code'; readonly text: string; readonly lang?: string }
  | { readonly kind: 'quote'; readonly blocks: readonly Block[] }
  | { readonly kind: 'list'; readonly ordered: boolean; readonly start: number; readonly items: readonly (readonly Inline[])[] }

/* ------------------------------------------------------------------ */
/* Links                                                               */
/* ------------------------------------------------------------------ */

const SAFE_SCHEME = /^(?:https?:|mailto:)/i

/**
 * The URL we are willing to navigate to, or undefined.
 *
 * Two rules, both absolute. The scheme must be http, https or mailto — a
 * "javascript:" URL in a comment is a stored XSS with extra steps. And the URL
 * may not contain whitespace or a control character, because that is precisely
 * how such a URL sneaks past a scheme test ("java\nscript:alert(1)" is a
 * working javascript URL in every browser). We refuse rather than sanitise:
 * stripping characters out of a hostile URL is a game we would eventually lose,
 * and showing the author's text instead loses nothing.
 *
 * safeHref : String -> String | undefined
 */
export function safeHref(raw: string): string | undefined {
  const url = raw.trim()
  if (url.length === 0) return undefined
  if (/[\u0000-\u0020\u007f]/.test(url)) return undefined
  return SAFE_SCHEME.test(url) ? url : undefined
}

/* ------------------------------------------------------------------ */
/* Inline                                                              */
/* ------------------------------------------------------------------ */

/** Characters a backslash may make literal. */
const ESCAPABLE = '\\`*[]#>-'

/** A bare URL, matched where the scanner stands rather than searched for. */
const AUTOLINK = /https?:\/\/[^\s<>()[\]`]+/y

/** Trailing characters that end a sentence rather than a URL. */
const URL_TAIL = '.,;:!?'

interface Taken { readonly node: Inline; readonly next: number }

/**
 * Parse one run of text into Inlines.
 *
 * A single left-to-right scan, so the construct that STARTS first wins. That is
 * what keeps a path inside "[[…]]" from being read as emphasis when it contains
 * an asterisk, without any precedence table to get wrong.
 *
 * parseInline : String -> [Inline]
 */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let buffer = ''

  const flush = (): void => {
    if (buffer.length > 0) pushText(out, buffer)
    buffer = ''
  }
  const take = (t: Taken | undefined): boolean => {
    if (!t) return false
    flush()
    out.push(t.node)
    i = t.next
    return true
  }

  let i = 0
  while (i < text.length) {
    const ch = text[i]

    if (ch === '\\' && i + 1 < text.length && ESCAPABLE.includes(text[i + 1])) {
      buffer += text[i + 1]
      i += 2
      continue
    }
    if (ch === '\n') {
      flush()
      out.push({ kind: 'break' })
      i += 1
      continue
    }
    if (ch === '`' && take(takeCode(text, i))) continue
    if (ch === '[' && text.startsWith(FILE_REF_OPEN, i) && take(takeFileRef(text, i))) continue
    if (ch === '[' && take(takeLink(text, i))) continue
    if (ch === '*' && text.startsWith('**', i) && take(takeEmphasis(text, i, '**'))) continue
    if (ch === '*' && take(takeEmphasis(text, i, '*'))) continue
    if (ch === 'h' && isWordStart(text, i) && take(takeAutolink(text, i))) continue

    buffer += ch
    i += 1
  }

  flush()
  return out
}

/** True when nothing word-like precedes position i. */
function isWordStart(text: string, i: number): boolean {
  return i === 0 || /[\s(<]/.test(text[i - 1])
}

/**
 * Split plain words into text and mention nodes.
 *
 * Mentions are found by @lattice/core's `segmentBody` — the same rules the
 * server uses to decide who gets notified — so a handle this draws as a person
 * is a person the server also found.
 *
 * The converse is not quite true, and deliberately so: the server reads the
 * whole body, while this runs on plain words only, so "`@prerana`" inside a
 * code span is drawn as type and still notifies her. Erring towards telling
 * somebody they were named is the safe direction of the two.
 */
function pushText(out: Inline[], text: string): void {
  for (const segment of segmentBody(text)) {
    if (segment.userId) out.push({ kind: 'mention', userId: segment.userId, text: segment.text })
    else if (segment.text.length > 0) out.push({ kind: 'text', text: segment.text })
  }
}

/** `code`, delimited by a run of n backticks and closed by the same run. */
function takeCode(text: string, i: number): Taken | undefined {
  let n = 0
  while (text[i + n] === '`') n += 1
  const fence = '`'.repeat(n)
  const close = text.indexOf(fence, i + n)
  if (close === -1) return undefined
  const inner = text.slice(i + n, close)
  if (inner.trim().length === 0) return undefined
  // A code span is one run of type; a newline inside it becomes a space.
  return { node: { kind: 'code', text: inner.replace(/\n/g, ' ') }, next: close + n }
}

/** [[path]] — a reference to a file in this comment's project. */
function takeFileRef(text: string, i: number): Taken | undefined {
  const close = text.indexOf(FILE_REF_CLOSE, i + FILE_REF_OPEN.length)
  if (close === -1) return undefined
  const path = parseFileRefPath(text.slice(i + FILE_REF_OPEN.length, close))
  if (!path) return undefined
  return { node: { kind: 'file', path }, next: close + FILE_REF_CLOSE.length }
}

/** [label](url), and only when the url is one we would follow. */
function takeLink(text: string, i: number): Taken | undefined {
  const close = text.indexOf(']', i + 1)
  if (close === -1 || text[close + 1] !== '(') return undefined
  const end = text.indexOf(')', close + 2)
  if (end === -1) return undefined
  const href = safeHref(text.slice(close + 2, end))
  if (!href) return undefined
  const label = text.slice(i + 1, close)
  return {
    node: {
      kind: 'link',
      href,
      children: label.length > 0 ? parseInline(label) : [{ kind: 'text', text: href }],
    },
    next: end + 1,
  }
}

/**
 * *italic* or **bold**.
 *
 * The character after the opening marker and the character before the closing
 * one must not be whitespace, which is what stops "2 * 3 * 4" from turning a
 * tolerance calculation into italics.
 */
function takeEmphasis(text: string, i: number, marker: '*' | '**'): Taken | undefined {
  const from = i + marker.length
  if (from >= text.length || /\s/.test(text[from])) return undefined

  let at = from
  for (;;) {
    const close = text.indexOf(marker, at)
    if (close === -1 || close <= from) return undefined
    if (/\s/.test(text[close - 1])) { at = close + marker.length; continue }
    return {
      node: {
        kind: marker === '**' ? 'strong' : 'em',
        children: parseInline(text.slice(from, close)),
      },
      next: close + marker.length,
    }
  }
}

/** A bare URL somebody pasted. */
function takeAutolink(text: string, i: number): Taken | undefined {
  AUTOLINK.lastIndex = i
  const match = AUTOLINK.exec(text)
  if (!match) return undefined
  let url = match[0]
  while (url.length > 0 && URL_TAIL.includes(url[url.length - 1])) url = url.slice(0, -1)
  const href = safeHref(url)
  if (!href) return undefined
  return { node: { kind: 'link', href, children: [{ kind: 'text', text: url }] }, next: i + url.length }
}

/* ------------------------------------------------------------------ */
/* Blocks                                                              */
/* ------------------------------------------------------------------ */

const FENCE = /^ {0,3}```\s*([A-Za-z0-9_+#-]*)\s*$/
const FENCE_END = /^ {0,3}```\s*$/
const HEADING = /^ {0,3}(#{1,6})\s+(.*)$/
const QUOTE = /^ {0,3}>\s?(.*)$/
const BULLET = /^ {0,3}[-*+]\s+(.*)$/
const ORDERED = /^ {0,3}(\d{1,9})[.)]\s+(.*)$/

/** True when a line begins something other than the paragraph in progress. */
const startsBlock = (line: string): boolean =>
  FENCE.test(line) || HEADING.test(line) || QUOTE.test(line) ||
  BULLET.test(line) || ORDERED.test(line)

/**
 * DATA DEFINITION
 *   A Depth is a Natural, how many blockquotes enclose the lines being parsed.
 *   Interpretation: 0 at the top of a body, one more inside each ">".
 *
 * WHY IT IS BOUNDED
 *   A quote holds blocks, so both parsing and RENDERING recur once per level,
 *   and a body may be 4000 characters: ">".repeat(3999) is a comment the server
 *   accepts and it recurs 3999 times — deep enough to exhaust the JavaScript
 *   stack. That throw happens inside render, Lattice has no error boundary, and
 *   the body is already in Postgres, so one such comment would blank the app for
 *   everyone in the project on every load until somebody edited the database.
 *   Nobody quotes six deep; past this the rest is shown as the text it is.
 */
const MAX_QUOTE_DEPTH = 6

/**
 * Parse a comment body into Blocks.
 *
 * Total: every input is some sequence of blocks, and text that is not valid
 * markdown is a paragraph of itself rather than an error. A comment must always
 * render, even the one somebody pasted a stack trace into.
 *
 * parseMarkdown : String -> [Block]
 */
export function parseMarkdown(source: string): Block[] {
  return parseBlocks(source.replace(/\r\n?/g, '\n').split('\n'), 0)
}

function parseBlocks(lines: readonly string[], depth: number): Block[] {
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim().length === 0) { i += 1; continue }

    const fence = FENCE.exec(line)
    if (fence) {
      const body: string[] = []
      i += 1
      while (i < lines.length && !FENCE_END.test(lines[i])) { body.push(lines[i]); i += 1 }
      i += 1 // the closing fence, or one past the end when it was never closed
      blocks.push({ kind: 'code', text: body.join('\n'), ...(fence[1] ? { lang: fence[1] } : {}) })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, children: parseInline(heading[2].trim()) })
      i += 1
      continue
    }

    if (QUOTE.test(line)) {
      const raw: string[] = []
      const quoted: string[] = []
      while (i < lines.length) {
        const q = QUOTE.exec(lines[i])
        if (!q) break
        raw.push(lines[i])
        quoted.push(q[1])
        i += 1
      }
      blocks.push(depth < MAX_QUOTE_DEPTH
        ? { kind: 'quote', blocks: parseBlocks(quoted, depth + 1) }
        // At the cap the ">" characters stop being structure and go back to
        // being characters, which is both safe and what they look like anyway.
        : { kind: 'paragraph', children: parseInline(raw.join('\n')) })
      continue
    }

    if (BULLET.test(line) || ORDERED.test(line)) {
      const ordered = ORDERED.test(line)
      const start = ordered ? Number((ORDERED.exec(line) as RegExpExecArray)[1]) : 1
      const items: Inline[][] = []
      while (i < lines.length) {
        const match = ordered ? ORDERED.exec(lines[i]) : BULLET.exec(lines[i])
        if (!match) break
        items.push(parseInline((ordered ? match[2] : match[1]).trim()))
        i += 1
      }
      blocks.push({ kind: 'list', ordered, start, items })
      continue
    }

    const paragraph: string[] = []
    while (i < lines.length && lines[i].trim().length > 0 && !startsBlock(lines[i])) {
      paragraph.push(lines[i])
      i += 1
    }
    blocks.push({ kind: 'paragraph', children: parseInline(paragraph.join('\n')) })
  }

  return blocks
}

/* ------------------------------------------------------------------ */
/* Plain text                                                          */
/* ------------------------------------------------------------------ */

/**
 * The words of a body with the markup taken out, for a one-line preview.
 * A file reference reads as its own name, because "Roof.PDF" is what the author
 * meant and "[[Drawing/Roof.PDF]]" is what they had to type.
 * plainTextOf : [Block] -> String
 */
export function plainTextOf(blocks: readonly Block[]): string {
  const inlines = (nodes: readonly Inline[]): string => nodes.map(one).join('')
  const one = (node: Inline): string => {
    switch (node.kind) {
      case 'text': return node.text
      case 'code': return node.text
      case 'mention': return node.text
      case 'file': return node.path.split('/').pop() ?? node.path
      case 'break': return ' '
      case 'strong': case 'em': case 'link': return inlines(node.children)
    }
  }
  const block = (b: Block): string => {
    switch (b.kind) {
      case 'paragraph': case 'heading': return inlines(b.children)
      case 'code': return b.text
      case 'quote': return b.blocks.map(block).join(' ')
      case 'list': return b.items.map(inlines).join(' ')
    }
  }
  return blocks.map(block).join(' ').replace(/\s+/g, ' ').trim()
}
